import { Ty } from '@cotera/era';
import _ from 'lodash';

/**
 * JSONNode represents the structure of a response shape in the type builder.
 * This is a simplified type system used for defining output shapes for LLM agents.
 *
 * **Object `fields` and `name` (critical for LLM-authored shapes):**
 * `responseShapeToShorthand` reads each entry in `object.fields` and uses the child
 * node's **`name`** as the **public JSON property name** in the output type—not the
 * record key. Keys in `fields` are often opaque ids in the UI; when authoring JSON
 * by hand, use readable keys if you want, but **always** set `name` on every value
 * in `fields` to the intended output field (e.g. `summary`, `order_id`).
 *
 * Example (two top-level string fields):
 * `{ "t":"object","nullable":false,"name":"Result","fields":{
 *    "a":{ "t":"string","nullable":false,"name":"title" },
 *    "b":{ "t":"string","nullable":false,"name":"body" }
 * }}`
 */

type BaseNode = {
  nullable?: boolean;
};

export type JSONNodeObject = BaseNode & {
  t: 'object';
  /** Optional label; output field names come from each `fields` value's `name`. */
  name?: string;
  fields: Record<string, JSONNode & { name: string }>;
  nullable?: boolean;
};

export type JSONNodeArray = BaseNode & {
  t: 'array';
  items: {
    item: JSONNode;
  };
  /** When this array is a value inside `object.fields`, must match the output property name. */
  name?: string;
  nullable?: boolean;
};

export type JSONNodeEnum = BaseNode & {
  t: 'enum';
  values: string[];
  /** When this enum is a value inside `object.fields`, must match the output property name. */
  name?: string;
  nullable?: boolean;
};

export type JSONNodePrimitive = BaseNode & {
  t: 'string' | 'int' | 'float' | 'boolean' | 'timestamp';
  /** When this primitive is a value inside `object.fields`, must match the output property name. */
  name?: string;
  nullable?: boolean;
};

export type JSONNode =
  | JSONNodeObject
  | JSONNodeArray
  | JSONNodePrimitive
  | JSONNodeEnum;

const PRIMITIVE_TYPES: readonly JSONNodePrimitive['t'][] = [
  'string',
  'int',
  'float',
  'boolean',
  'timestamp',
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNodeType = (t: unknown): t is JSONNode['t'] =>
  t === 'object' ||
  t === 'array' ||
  t === 'enum' ||
  PRIMITIVE_TYPES.includes(t as JSONNodePrimitive['t']);

/** `t` is missing or unrecognised: infer it from the keys that are present. */
const inferNodeType = (node: Record<string, unknown>): JSONNode['t'] => {
  if (isRecord(node['fields'])) {
    return 'object';
  }
  if (node['items'] !== undefined) {
    return 'array';
  }
  if (Array.isArray(node['values'])) {
    return 'enum';
  }
  return 'string';
};

/**
 * The element node of an array, tolerating the shapes that show up in the wild:
 * the canonical `{ items: { item } }`, a bare `{ items: node }`, and
 * `{ items: [node] }`.
 */
const arrayItemOf = (items: unknown): unknown => {
  if (Array.isArray(items)) {
    return items[0];
  }
  if (isRecord(items) && 'item' in items) {
    return items['item'];
  }
  return items;
};

const normalizeFields = (input: unknown): JSONNodeObject['fields'] => {
  if (!isRecord(input)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(input).map(([key, field]) => {
      const node = normalizeJsonNode(field);
      // Values in `fields` carry the output property name. Fall back to the
      // record key only when the payload omitted `name` altogether — an empty
      // name is intentional, the builder creates fields with a blank name for
      // the user to fill in.
      return [key, { ...node, name: node.name ?? key }];
    })
  );
};

const normalizeEnumValues = (input: unknown): string[] =>
  Array.isArray(input)
    ? input.filter((value): value is string => typeof value === 'string')
    : [];

/**
 * Coerces an untrusted payload into a well formed JSONNode.
 *
 * Response shapes are persisted as opaque TipTap node attrs and are routinely
 * authored by LLMs, so a stored shape can carry a bare type name where a node
 * belongs (`{ fields: { a: 'object' } }`), an object without `fields`, or an
 * array whose `items` was not wrapped in `{ item }`. Consumers walk these
 * structurally, so repair them up front instead of letting the walk land on a
 * string. Anything unrecognisable degrades to a nullable-free string node, and
 * unknown keys are dropped.
 */
export function normalizeJsonNode(input: unknown): JSONNode {
  const node: Record<string, unknown> = isRecord(input)
    ? input
    : typeof input === 'string'
    ? { t: input }
    : {};

  const rawT = node['t'];
  const t = isNodeType(rawT) ? rawT : inferNodeType(node);
  const nullable = node['nullable'] === true;
  const name = typeof node['name'] === 'string' ? { name: node['name'] } : {};

  switch (t) {
    case 'object':
      return { t, ...name, nullable, fields: normalizeFields(node['fields']) };
    case 'array':
      return {
        t,
        ...name,
        nullable,
        items: { item: normalizeJsonNode(arrayItemOf(node['items'])) },
      };
    case 'enum':
      return {
        t,
        ...name,
        nullable,
        values: normalizeEnumValues(node['values']),
      };
    default:
      return { t, ...name, nullable };
  }
}

/**
 * Converts a JSONNode to a Ty.ExtendedAttributeType.
 * Uses the Ty helper functions (ty/nn, structOf, arrayOf, enumOf) for proper type construction.
 */
export function responseShapeToShorthand(
  responseShape: JSONNode
): Ty.ExtendedAttributeType {
  const tyWrapper = responseShape.nullable ? Ty.ty : Ty.nn;
  switch (responseShape.t) {
    case 'object':
      return tyWrapper(
        Ty.structOf(
          Object.fromEntries(
            Object.entries(responseShape.fields)
              .sort(([, a], [, b]) => a.name.localeCompare(b.name))
              .map(([_, item]) => [item.name, responseShapeToShorthand(item)])
          )
        )
      );
    case 'array':
      return tyWrapper(
        Ty.arrayOf(responseShapeToShorthand(responseShape.items.item))
      );
    case 'string':
      return tyWrapper('string');
    case 'float':
      return tyWrapper('float');
    case 'int':
      return tyWrapper('int');
    case 'boolean':
      return tyWrapper('boolean');
    case 'timestamp':
      return tyWrapper('timestamp');
    case 'enum':
      return tyWrapper(Ty.enumOf(responseShape.values));
    default:
      throw new Error(`Unknown type: ${(responseShape as any).t}`);
  }
}

/**
 * Converts a Ty.ExtendedAttributeType back to a JSONNode.
 * This is the inverse operation of responseShapeToShorthand.
 */
export function shorthandToResponseShape(
  ty: Ty.ExtendedAttributeType
): JSONNode {
  if (ty.ty.k === 'primitive') {
    switch (ty.ty.t) {
      case 'int':
        return { t: 'int', nullable: ty.nullable };
      case 'float':
        return { t: 'float', nullable: ty.nullable };
      case 'boolean':
        return { t: 'boolean', nullable: ty.nullable };
      case 'string':
        return { t: 'string', nullable: ty.nullable };
      case 'day':
      case 'month':
      case 'year':
      case 'timestamp':
        return { t: 'timestamp', nullable: ty.nullable };
      case 'super':
        throw new Error("Cannot map 'super' to ResponseShape.");
      default:
        throw new Error(`Unknown primitive type: ${ty}`);
    }
  }

  // Handle array types
  if (ty.ty.k === 'array') {
    return {
      t: 'array',
      items: {
        item: shorthandToResponseShape(ty.ty.t),
      },
      nullable: ty.nullable,
    };
  }

  // Handle struct types
  if (ty.ty.k === 'struct') {
    return {
      t: 'object',
      fields: _.mapValues(ty.ty.fields, (v, key) => ({
        ...shorthandToResponseShape(v),
        name: key,
      })),
      nullable: ty.nullable,
    };
  }

  // Handle enum types
  if (ty.ty.k === 'enum') {
    return { t: 'enum', values: [...ty.ty.t], nullable: ty.nullable };
  }

  throw new Error(`Unsupported ExtendedAttributeType: ${ty.ty.k}`);
}
